import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { InsertAltTextDialog } from '@lumapps/lumx-dialogs/components/AltTextDialog';
import { LUMX_DIALOGS } from '@lumapps/lumx-dialogs/keys';
import {
    imageAlignmentCenter,
    imageAlignmentFull,
    imageAlignmentLeft,
    imageAlignmentRight,
} from '@lumapps/lumx/custom-icons';
import { mdiClose, mdiLinkVariant, mdiLinkVariantOff } from '@lumapps/lumx/icons';
import { FlexBox, ProgressCircular, Size, useTheme } from '@lumapps/lumx/react';
import { useDimensions } from '@lumapps/responsive';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { isHotKey } from '@lumapps/utils/browser/isHotKey';
import { scrollIntoView } from '@lumapps/utils/browser/scrollIntoView';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { isColumn } from '@lumapps/wrex-columns/utils/isColumn';
import { WREX_IMAGE } from '@lumapps/wrex-image/keys';
import { ElementToolbar } from '@lumapps/wrex/components/ElementToolbar';
import { WREX_CORE } from '@lumapps/wrex/keys';
import { Editor, Path, ReactEditor, useSelected, useSlateStatic } from '@lumapps/wrex/slate';
import { findParent } from '@lumapps/wrex/slate/utils/findParent';
import { focusAt } from '@lumapps/wrex/slate/utils/focusAt';
import { getSiblingPath } from '@lumapps/wrex/slate/utils/getSibling';
import { useInlineVoid } from '@lumapps/wrex/slate/utils/useInlineVoid';
import { ElementRender, ToolbarItem as ToolbarItemType } from '@lumapps/wrex/types';

import { CLASSNAME, IMAGE_ALIGNMENTS, IMAGE_FEATURES, IMAGE_GROUP, IMAGE_WIDTHS } from '../../../constants';
import { WREX_ENHANCED_IMAGE } from '../../../keys';
import { ImageEditor, ImageGroupElement } from '../../../types';
import { changeImageWidth } from '../../../utils/changeImageWidth';
import { isAlignedImageWrapper } from '../../../utils/isAlignedImageWrapper';
import { ImageGroup } from '../../blocks/ImageGroup';
import { LinkInlineDialog } from '../LinkInlineDialog';
import { useAutoWidth } from './useAutoWidth';

import './index.scss';

/**
 * Wraps the Image content to add edition-related behaviors.
 */
export const EditableEnhancedImage: ElementRender<ImageGroupElement, HTMLDivElement> = (props) => {
    const { children, element = {} as ImageGroupElement, elementRef, readOnly: isReadOnly, ...forwardedProps } = props;
    const theme = useTheme();

    const { translateKey } = useTranslate();
    const { element: elementClass, block: blockClass } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(CLASSNAME);
    const editorStatic = useSlateStatic() as ReactEditor & ImageEditor;
    const selected = useSelected();
    const { onDelete } = useInlineVoid(editorStatic, element);

    // Focus caption input on mount only on new images (having blob URL).
    const inputRef = useRef<HTMLInputElement>(null);

    const [captionValue, setCaptionValue] = useState(element?.title);
    const [isAltTextDialogOpen, , closeAltTextDialog, openAltTextDialog] = useBooleanState(false);
    const [isLinkDialogOpen, setIsLinkDialogOpen, closeLinkDialog, openLinkDialog] = useBooleanState(false);
    // Used for toolbar responsive, 350 being the width in which the toolbar overflow the image in hungarian
    const { currentBreakpoint, ref: dimRef } = useDimensions({ breakpoints: { reduced: 0, full: 350 } });

    const path = ReactEditor.findPath(editorStatic, element) as Path;
    const isAltPresent = !!element?.images[0].alt;
    const isLinkPresent = !!element?.images[0].link;

    const wrapper = findParent(editorStatic, path, isAlignedImageWrapper);
    const columnParent = findParent(editorStatic, path, isColumn);
    const isAlignedImage = !!wrapper;

    const imgRef = useAutoWidth({ element, path, isAlignedImage });
    const menuBarRef = useRef<HTMLDivElement>(null);

    const staticOptions: ToolbarItemType[] = [
        {
            type: 'option',
            icon: mdiClose,
            itemKey: 'delete-image',
            label: translateKey(GLOBAL.DELETE),
            variant: 'icon',
            onClick: onDelete,
            otherProps: get({ element: 'button', action: 'delete-image' }),
        },
    ];

    const section: ToolbarItemType = {
        type: 'section',
        label: '',
        itemKey: 'image-options-section',
        childrenOptions: [],
    };

    const hasAlignment = editorStatic.isImageFeatureEnabled(IMAGE_FEATURES.alignment);
    const hasWidthFeature = editorStatic.isImageFeatureEnabled(IMAGE_FEATURES.width);
    const hasLinkFeature = editorStatic.isImageFeatureEnabled(IMAGE_FEATURES.link);
    if (hasAlignment || hasWidthFeature) {
        const alignment = wrapper?.[0].alignment;
        if (hasAlignment) {
            section.childrenOptions.push({
                type: 'toggle-option',
                icon: imageAlignmentLeft,
                itemKey: 'alignment-left',
                variant: 'icon',
                label: translateKey(WREX_CORE.ALIGN_TO_LEFT),
                isChecked: alignment === IMAGE_ALIGNMENTS.left,
                onClick: () => editorStatic.changeImageAlignment(path, IMAGE_ALIGNMENTS.left),
                otherProps: get({ element: 'button', action: 'alignment-left' }),
            });
        }
        if (hasWidthFeature) {
            section.childrenOptions.push(
                {
                    type: 'toggle-option',
                    icon: imageAlignmentCenter,
                    itemKey: 'alignment-center',
                    variant: 'icon',
                    label: translateKey(WREX_CORE.ALIGN_CENTER),
                    isChecked: !alignment && element?.width === IMAGE_WIDTHS.halfWidth,
                    onClick: () => changeImageWidth(editorStatic, path, IMAGE_WIDTHS.halfWidth),
                    otherProps: get({ element: 'button', action: 'alignment-center' }),
                },
                {
                    type: 'toggle-option',
                    icon: imageAlignmentFull,
                    itemKey: 'alignment-full',
                    variant: 'icon',
                    label: translateKey(WREX_ENHANCED_IMAGE.FULL_WIDTH),
                    isChecked: !alignment && element?.width === IMAGE_WIDTHS.fullWidth,
                    onClick: () => changeImageWidth(editorStatic, path, IMAGE_WIDTHS.fullWidth),
                    otherProps: get({ element: 'button', action: 'alignment-full' }),
                },
            );
        }

        if (hasAlignment) {
            section.childrenOptions.push({
                type: 'toggle-option',
                icon: imageAlignmentRight,
                itemKey: 'alignment-right',
                variant: 'icon',
                label: translateKey(WREX_CORE.ALIGN_TO_RIGHT),
                isChecked: alignment === IMAGE_ALIGNMENTS.right,
                onClick: () => editorStatic.changeImageAlignment(path, IMAGE_ALIGNMENTS.right),
                otherProps: get({ element: 'button', action: 'alignment-right' }),
            });
        }

        section.childrenOptions.push({ type: 'divider', itemKey: 'divider1' });
    }

    const insertLink = useCallback(
        (link: string | undefined) => {
            editorStatic.updateImageNode(
                {
                    images: [
                        {
                            ...element.images[0],
                            link,
                        },
                    ],
                },
                path,
            );
            closeLinkDialog();
        },
        [closeLinkDialog, editorStatic, element, path],
    );

    const removeLink = useCallback(() => {
        insertLink(undefined);
    }, [insertLink]);

    if (hasLinkFeature) {
        section.childrenOptions.push({
            type: 'option',
            icon: isLinkPresent ? mdiLinkVariantOff : mdiLinkVariant,
            itemKey: 'image-link',
            variant: 'icon',
            label: translateKey(isLinkPresent ? WREX_ENHANCED_IMAGE.REMOVE_LINK : WREX_ENHANCED_IMAGE.APPLY_LINK),
            onClick: isLinkPresent
                ? removeLink
                : () => {
                      focusAt(editorStatic, path);
                      openLinkDialog();
                  },

            otherProps: get({
                element: 'button',
                action: isLinkPresent ? 'remove-link' : 'add-link',
            }),
        });
        section.childrenOptions.push({ type: 'divider', itemKey: 'divider2' });
    }

    section.childrenOptions.push({
        type: 'option',
        itemKey: 'image-alt',
        label: translateKey(isAltPresent ? WREX_IMAGE.INSERT_ALT_TEXT_EDIT : WREX_IMAGE.INSERT_ALT_TEXT_ADD),
        onClick: openAltTextDialog,
        otherProps: get({
            element: 'button',
            action: isAltPresent ? 'open-edit-alt-dialog' : 'open-add-alt-dialog',
        }),
    });

    const onCaptionChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const pathRef = Editor.pathRef(editorStatic, path);
            editorStatic.updateImageNode(
                {
                    title: event.target.value,
                },
                pathRef,
            );
            setCaptionValue(event.target.value);
        },
        [editorStatic, path],
    );

    const onCaptionKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!element) {
                return;
            }
            let focusLocation: Path | null = null;
            if (isHotKey('Enter', event as any) || isHotKey('ArrowDown', event as any)) {
                // Enter OR ArrowDown => Focus previous element
                focusLocation = getSiblingPath(ReactEditor.findPath(editorStatic, element), 'after');
            } else if (isHotKey('ArrowUp', event as any)) {
                // ArrowUp => Focus previous element
                focusLocation = getSiblingPath(ReactEditor.findPath(editorStatic, element), 'before');
            }
            if (focusLocation) {
                event.preventDefault();
                focusAt(editorStatic, focusLocation);
            }
        },
        [editorStatic, element],
    );

    const insertAltText = useCallback(
        (altText: string) => {
            editorStatic.updateImageNode(
                {
                    images: [
                        {
                            ...element.images[0],
                            alt: altText,
                        },
                    ],
                },
                path,
            );
            closeAltTextDialog();
        },
        [closeAltTextDialog, editorStatic, element, path],
    );

    useEffect(() => {
        const { current } = inputRef;

        if (!element.shouldFocusCaptionOnMount || !current) {
            return;
        }

        current.focus();
        // Use of a setTimeout to handle the little
        // delay between the set of the image height.
        setTimeout(() => {
            scrollIntoView(current);
        }, 100);
    }, [element]);

    return (
        <div
            className={elementClass('main-wrapper', { editable: true, selected })}
            ref={elementRef}
            {...forwardedProps}
        >
            <ImageGroup
                isInEditionMode
                element={element}
                className={blockClass({ editable: true, selected })}
                elementRef={dimRef}
                imgRef={imgRef}
            >
                {children}

                {!element?.isLoading && (
                    <input
                        ref={inputRef}
                        contentEditable={false}
                        value={captionValue}
                        onChange={onCaptionChange}
                        onKeyDown={onCaptionKeyDown}
                        placeholder={translateKey(WREX_IMAGE.IMAGE_CAPTION_PLACEHOLDER)}
                        className={elementClass('caption-input', {
                            [`theme-${theme}`]: !!theme,
                        })}
                        readOnly={isReadOnly}
                    />
                )}

                {!element?.isLoading && (
                    <ElementToolbar
                        className={elementClass('actions')}
                        toolbarOptions={[section]}
                        staticOptions={staticOptions}
                        dataScope={CLASSNAME}
                        toolbarAriaLabel={translateKey(WREX_ENHANCED_IMAGE.TOOLBAR)}
                        currentBreakpoint={currentBreakpoint}
                        menuBarRef={menuBarRef}
                        size={columnParent ? Size.s : Size.m}
                    />
                )}
            </ImageGroup>
            {element?.isLoading && (
                <FlexBox fillSpace hAlign="center" vAlign="center" className={elementClass('loader-wrapper')}>
                    <ProgressCircular />
                </FlexBox>
            )}
            <InsertAltTextDialog
                isOpen={isAltTextDialogOpen}
                size="tiny"
                currentAlt={element?.images[0].alt || ''}
                image={element.images[0].tempSrc || element.images[0].src}
                onChange={insertAltText}
                onClose={closeAltTextDialog}
                scope="wrex--alt-text-dialog"
            />
            {hasLinkFeature && !isLinkPresent && (
                <LinkInlineDialog
                    inputPlaceholder={translateKey(LUMX_DIALOGS.LINK_DIALOG_INPUT_PLACEHOLDER)}
                    isOpen={isLinkDialogOpen}
                    currentLink={element?.images[0].link || ''}
                    onChange={insertLink}
                    setIsOpen={setIsLinkDialogOpen}
                    anchorRef={menuBarRef}
                />
            )}
        </div>
    );
};
EditableEnhancedImage.displayName = IMAGE_GROUP;
